import React, { useEffect, useState } from "react";
import { formatPhone } from "../../utils/formatPhone";
import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const CreateUserForm = ({ onFinish }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    role_id: "",
    hourly_rate: "",
    mileage_rate: "",
    tenant_id: "",
    has_placement_access: false,
  });

  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id || "";
  const isPrimary = user?.is_primary || false;

  const [roles, setRoles] = useState([]);
  const [states, setStates] = useState([]);
  const [message, setMessage] = useState("");
  const [tenants, setTenants] = useState([]);
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);

  // Load roles and states when component loads
  useEffect(() => {
    console.log("ðŸ‘€ useEffect running, tenant_id =", tenant_id);

    // Fetch roles (global - no tenant_id needed)
    secureAxios
      .get("/api/roles")
      .then((res) => {
        setRoles(res.data);
      })
      .catch((err) => console.error("âŒ Error loading roles", err));

    // Fetch all states globally
    secureAxios
      .get("/api/states")
      .then((res) => {
        setStates(res.data);
      })
      .catch((err) => {
        console.error("âŒ Error loading states", err.message);
      });
  }, [tenant_id]);

  useEffect(() => {
    if (isPrimary && user) {
      secureAxios
        .get("/api/tenants", {
          params: { user: JSON.stringify(user) },
        })
        .then((res) => setTenants(res.data))
        .catch((err) => console.error("âŒ Failed to load tenants:", err));
    }
  }, [isPrimary, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = type === "checkbox" ? checked : value;
    if (name === "phone_number") {
      newValue = formatPhone(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!privacyAcknowledged) {
      setMessage("âŒ Please acknowledge the privacy notice before proceeding.");
      return;
    }

    setMessage("Creating user...");

    const cleanedPhone = formData.phone_number.replace(/\D/g, "");

    const payload = {
      ...formData,
      tenant_id: isPrimary ? formData.tenant_id : tenant_id,
      phone_number: cleanedPhone,
      created_by: user?.user_id || "system",
      entity_type: "user",
      hourly_rate: parseFloat(formData.hourly_rate) || 0,
      mileage_rate: parseFloat(formData.mileage_rate) || 0,
      user: JSON.stringify(user),
      privacy_acknowledged: privacyAcknowledged,
      privacy_acknowledged_at: new Date().toISOString(),
    };

    try {
      await secureAxios.post("/api/users", payload);
      setMessage("âœ… User created and email sent!");

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone_number: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state_id: "",
        postal_code: "",
        role_id: "",
        hourly_rate: "",
        mileage_rate: "",
        tenant_id: "",
        has_placement_access: false,
      });
      setPrivacyAcknowledged(false);

      // Wait a moment to show success message, then go back to list
      setTimeout(() => {
        if (onFinish) {
          onFinish();
        }
      }, 1500);
    } catch (err) {
      console.error("âŒ Submission error:", err);
      if (err.response?.status === 401) {
        setMessage("âŒ Unauthorized. Please log in again.");
      } else {
        setMessage("âŒ Error creating user.");
      }
    }
  };

  return (
    <div>
      <h2>Create New User</h2>

      {/* HIPAA Privacy Notice */}
      <div className="alert alert-info mb-4">
        <h5 className="alert-heading">
          <i className="bi bi-shield-lock me-2"></i>
          Privacy & Security Notice
        </h5>
        <p className="mb-2">
          This system contains Protected Health Information (PHI) and Personally
          Identifiable Information (PII). By creating this user account, you
          acknowledge that:
        </p>
        <ul className="mb-2">
          <li>All information entered will be encrypted and stored securely</li>
          <li>
            Access to this system is monitored and logged for security purposes
          </li>
          <li>
            Users must complete HIPAA training before accessing patient
            information
          </li>
          <li>
            Unauthorized access or disclosure of PHI may result in civil and
            criminal penalties
          </li>
          <li>User accounts are subject to periodic security reviews</li>
        </ul>
        <small className="text-muted">
          This system complies with HIPAA Privacy and Security Rules (45 CFR
          Parts 160, 162, and 164)
        </small>
      </div>

      {message && (
        <div
          className={`alert ${
            message.includes("âœ…") ? "alert-success" : "alert-danger"
          } alert-dismissible`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Name */}
        <div className="col-md-6">
          <label className="form-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        {/* Email */}
        <div className="col-md-6">
          <label className="form-label">
            Email <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <small className="text-muted">
            A secure setup link will be sent to this email address
          </small>
        </div>

        {isPrimary && (
          <div className="col-md-6">
            <label className="form-label">
              Tenant <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              name="tenant_id"
              value={formData.tenant_id || ""}
              onChange={handleChange}
              required
            >
              <option value="">Select Tenant</option>
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>
                  {t.tenant_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Role */}
        <div className="col-md-6">
          <label className="form-label">
            Role <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="role_id"
            value={formData.role_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Role</option>
            {roles.map((role) => (
              <option key={role.role_id} value={role.role_id}>
                {role.role}
              </option>
            ))}
          </select>
          <small className="text-muted">
            Role determines system access permissions
          </small>
        </div>

        {/* Phone Number */}
        <div className="col-md-6">
          <label className="form-label">
            Phone <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="phone_number"
            value={formData.phone_number || ""} // âœ… prevents undefined
            onChange={handleChange}
            required
          />
        </div>

        {/* Rate Fields - Side by Side */}
        <div className="col-md-6">
          <label className="form-label">Hourly Rate ($)</label>
          <input
            type="number"
            className="form-control"
            name="hourly_rate"
            value={formData.hourly_rate}
            onChange={handleChange}
            step="0.01"
            min="0"
            placeholder="0.00"
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Mileage Rate ($/mile)</label>
          <input
            type="number"
            className="form-control"
            name="mileage_rate"
            value={formData.mileage_rate}
            onChange={handleChange}
            step="0.01"
            min="0"
            placeholder="0.00"
          />
        </div>

        {/* Placement Management Access */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body py-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="has_placement_access"
                  id="has_placement_access"
                  checked={formData.has_placement_access}
                  onChange={handleChange}
                />
                <label
                  className="form-check-label"
                  htmlFor="has_placement_access"
                >
                  <strong>
                    <i className="bi bi-briefcase me-2"></i>
                    Placement Management Access
                  </strong>
                  <br />
                  <small className="text-muted">
                    Grant access to Placement Management features including:
                  </small>
                  <ul className="mb-0 mt-1 small text-muted">
                    <li>CRP-30: Job Placement Plan</li>
                    <li>CRP-31: Placement Services Assessment</li>
                    <li>CRP-32: Job Placement Follow-Up</li>
                    <li>CRP-33: Job Search Data Sheet</li>
                    <li>Contacts, Outreach, Placements, and Retention features</li>
                  </ul>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Address Line 1 */}
        <div className="col-12">
          <label className="form-label">
            Address Line 1 <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="address_line_1"
            value={formData.address_line_1}
            onChange={handleChange}
            required
          />
        </div>

        {/* Address Line 2 */}
        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            className="form-control"
            name="address_line_2"
            value={formData.address_line_2}
            onChange={handleChange}
          />
        </div>

        {/* City */}
        <div className="col-md-4">
          <label className="form-label">
            City <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
          />
        </div>

        {/* State */}
        <div className="col-md-4">
          <label className="form-label">
            State <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="state_id"
            value={formData.state_id || ""}
            onChange={handleChange}
            required
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.state_id} value={state.state_id}>
                {state.state_code} - {state.state_name}
              </option>
            ))}
          </select>
        </div>

        {/* ZIP */}
        <div className="col-md-4">
          <label className="form-label">
            ZIP Code <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            required
          />
        </div>

        {/* Privacy Acknowledgment */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="privacyAcknowledgment"
                  checked={privacyAcknowledged}
                  onChange={(e) => setPrivacyAcknowledged(e.target.checked)}
                  required
                />
                <label
                  className="form-check-label"
                  htmlFor="privacyAcknowledgment"
                >
                  <strong>Privacy Acknowledgment Required</strong>
                  <br />I acknowledge that:
                  <ul className="mt-2 mb-0">
                    <li>
                      This user will have access to Protected Health Information
                      (PHI)
                    </li>
                    <li>
                      The user must complete HIPAA training before system access
                    </li>
                    <li>
                      All system access and actions will be logged and monitored
                    </li>
                    <li>
                      The user is responsible for maintaining the
                      confidentiality of their credentials
                    </li>
                    <li>
                      Inappropriate access or disclosure of PHI may result in
                      termination and legal action
                    </li>
                  </ul>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Data Protection Notice */}
        <div className="col-12">
          <div className="alert alert-warning mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Data Protection Notice:</strong> All information entered is
            encrypted in transit and at rest. This system maintains detailed
            audit logs of all user activities in compliance with HIPAA
            requirements.
          </div>
        </div>

        {/* Submit */}
        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!privacyAcknowledged}
          >
            <i className="bi bi-person-plus me-2"></i>
            Create User Account
          </button>
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={onFinish}
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Footer Notice */}
      <div className="mt-4 text-muted small">
        <p>
          <i className="bi bi-info-circle me-1"></i>
          By creating this account, you affirm that you have the authority to
          grant system access and that the user will be bound by your
          organization's HIPAA policies and procedures.
        </p>
      </div>
    </div>
  );
};

export default CreateUserForm;